import React, { useState, useEffect, useCallback } from 'react';
import { Download, Play, RefreshCw, Trash2, CheckCircle, Clock, AlertCircle, FileJson, Database, Users, Star, Loader2, Info, Search, ChevronLeft, ChevronRight, Calendar, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CelebrityDatasetDownload() {
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [currentJob, setCurrentJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [includeKernel, setIncludeKernel] = useState(true);
  const [sampleSize, setSampleSize] = useState(0);
  
  // Browse state
  const [celebrities, setCelebrities] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCelebs, setTotalCelebs] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRating, setFilterRating] = useState('');
  const [filterProfession, setFilterProfession] = useState('');

  useEffect(() => {
    fetchDatasetInfo();
    fetchJobs();
    fetchCelebrities(1);
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, []);

  const fetchCelebrities = async (pageNum, search = searchQuery, rating = filterRating, profession = filterProfession) => {
    setBrowseLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pageNum);
      params.append('per_page', 20);
      if (search) params.append('search', search);
      if (rating) params.append('rating', rating);
      if (profession) params.append('profession', profession);
      
      const response = await fetch(`${API_URL}/api/celebrity-dataset/browse?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setCelebrities(data.celebrities);
        setTotalPages(data.total_pages);
        setTotalCelebs(data.total);
        setPage(pageNum);
      }
    } catch (err) {
      console.error('Error fetching celebrities:', err);
    } finally {
      setBrowseLoading(false);
    }
  };

  const handleSearch = () => {
    fetchCelebrities(1, searchQuery, filterRating, filterProfession);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchCelebrities(newPage);
    }
  };

  const fetchDatasetInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/celebrity-dataset/info`);
      const data = await response.json();
      if (data.success) {
        setDatasetInfo(data.dataset_info);
      }
    } catch (err) {
      console.error('Error fetching dataset info:', err);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/celebrity-dataset/jobs`);
      const data = await response.json();
      setJobs(data.jobs || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  };

  const startExport = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('include_kernel', includeKernel);
      if (sampleSize > 0) {
        params.append('sample_size', sampleSize);
      }

      const response = await fetch(`${API_URL}/api/celebrity-dataset/start-export?${params.toString()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        setCurrentJob(data.job_id);
        const interval = setInterval(() => pollJobStatus(data.job_id), 3000);
        setPollingInterval(interval);
        fetchJobs();
      } else {
        setError(data.detail || 'Failed to start export');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const response = await fetch(`${API_URL}/api/celebrity-dataset/status/${jobId}`);
      const data = await response.json();

      if (data.job?.status === 'completed' || data.job?.status === 'failed') {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        fetchJobs();
      }

      setJobs(prev => prev.map(job => 
        job.job_id === jobId ? { ...job, ...data.job } : job
      ));
    } catch (err) {
      console.error('Error polling job status:', err);
    }
  }, [pollingInterval]);

  const downloadFile = async (jobId) => {
    try {
      window.open(`${API_URL}/api/celebrity-dataset/download/${jobId}`, '_blank');
    } catch (err) {
      setError(err.message);
    }
  };

  const quickDownload = async () => {
    try {
      window.open(`${API_URL}/api/celebrity-dataset/quick-download`, '_blank');
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteJob = async (jobId) => {
    try {
      await fetch(`${API_URL}/api/celebrity-dataset/job/${jobId}`, {
        method: 'DELETE'
      });
      fetchJobs();
    } catch (err) {
      console.error('Error deleting job:', err);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-emerald-400" />;
      case 'processing': return <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />;
      case 'queued': return <Clock className="w-5 h-5 text-blue-400" />;
      case 'failed': return <AlertCircle className="w-5 h-5 text-red-400" />;
      default: return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
      case 'processing': return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
      case 'queued': return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
      case 'failed': return 'bg-red-500/10 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Database className="w-10 h-10 text-amber-400" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
              Celebrity Dataset Download
            </h1>
          </div>
          <p className="text-slate-400">
            Download 24,000+ AA-rated celebrities with complete astrological kernels
          </p>
        </div>

        {/* Dataset Info Card */}
        {datasetInfo && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-4">
              <Info className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-semibold">Dataset Information</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <Users className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{datasetInfo.total_celebrities?.toLocaleString()}</div>
                <div className="text-xs text-slate-400">Total Celebrities</div>
              </div>
              
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <Star className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{datasetInfo.ratings?.AA?.toLocaleString() || '0'}</div>
                <div className="text-xs text-slate-400">AA Rating</div>
              </div>
              
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <Star className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{datasetInfo.ratings?.A?.toLocaleString() || '0'}</div>
                <div className="text-xs text-slate-400">A Rating</div>
              </div>
              
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <FileJson className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">13+</div>
                <div className="text-xs text-slate-400">Kernel Sections</div>
              </div>
            </div>

            {/* Top Professions */}
            <div className="mb-4">
              <h3 className="text-sm font-medium text-slate-400 mb-2">Top Professions:</h3>
              <div className="flex flex-wrap gap-2">
                {datasetInfo.top_professions && Object.entries(datasetInfo.top_professions).slice(0, 10).map(([prof, count]) => (
                  <span key={prof} className="px-3 py-1 bg-slate-700/50 rounded-full text-xs text-slate-300">
                    {prof} ({count})
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Export Options */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-slate-700/50">
          <h2 className="text-lg font-semibold mb-4">Export Options</h2>
          
          <div className="space-y-4">
            {/* Include Kernel Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
              <div>
                <div className="font-medium">Include Astrological Kernels</div>
                <div className="text-sm text-slate-400">Generate complete kernel for each celebrity (takes longer)</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeKernel}
                  onChange={(e) => setIncludeKernel(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
              </label>
            </div>

            {/* Sample Size */}
            <div className="p-4 bg-slate-900/50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-medium">Sample Size (for testing)</div>
                  <div className="text-sm text-slate-400">Leave 0 for full dataset</div>
                </div>
                <input
                  type="number"
                  value={sampleSize}
                  onChange={(e) => setSampleSize(parseInt(e.target.value) || 0)}
                  min="0"
                  max="24000"
                  className="w-24 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button
              onClick={startExport}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="start-export-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Start Export {includeKernel ? '(with Kernels)' : '(Data Only)'}
                </>
              )}
            </button>
            
            <button
              onClick={quickDownload}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-all"
              data-testid="quick-download-btn"
            >
              <Download className="w-5 h-5" />
              Quick Download (No Kernels)
            </button>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
              {error}
            </div>
          )}
        </div>

        {/* Jobs List */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Export Jobs</h2>
            <button
              onClick={fetchJobs}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              data-testid="refresh-jobs-btn"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {jobs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileJson className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No export jobs yet. Start an export above.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map((job) => (
                <div
                  key={job.job_id}
                  className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/30"
                  data-testid={`job-${job.job_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(job.status)}
                      <div>
                        <div className="font-medium">Job: {job.job_id}</div>
                        <div className="text-sm text-slate-400">
                          {job.include_kernel ? 'With Kernels' : 'Data Only'}
                          {job.sample_size ? ` • Sample: ${job.sample_size}` : ' • Full Dataset'}
                        </div>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs border ${getStatusColor(job.status)}`}>
                      {job.status}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  {job.status === 'processing' && job.total > 0 && (
                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-slate-400 mb-1">
                        <span>{job.progress?.toLocaleString()} / {job.total?.toLocaleString()}</span>
                        <span>{Math.round((job.progress / job.total) * 100)}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div
                          className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(job.progress / job.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="text-sm text-slate-400 mb-3">{job.message}</div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {job.status === 'completed' && (
                      <button
                        onClick={() => downloadFile(job.job_id)}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
                        data-testid={`download-${job.job_id}`}
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    )}
                    <button
                      onClick={() => deleteJob(job.job_id)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                      data-testid={`delete-${job.job_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Browse Celebrities Section */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Browse All Celebrities</h2>
            <span className="text-sm text-slate-400">{totalCelebs.toLocaleString()} results</span>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                data-testid="search-input"
              />
            </div>
            <select
              value={filterRating}
              onChange={(e) => { setFilterRating(e.target.value); fetchCelebrities(1, searchQuery, e.target.value, filterProfession); }}
              className="px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
              data-testid="rating-filter"
            >
              <option value="">All Ratings</option>
              <option value="AA">AA Only</option>
              <option value="A">A Only</option>
            </select>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
              data-testid="search-btn"
            >
              Search
            </button>
          </div>

          {/* Celebrity List */}
          {browseLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
            </div>
          ) : (
            <div className="space-y-2">
              {celebrities.map((celeb) => (
                <div
                  key={celeb.id}
                  className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/30 hover:border-slate-600 transition-colors"
                  data-testid={`celeb-${celeb.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-white">{celeb.name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${celeb.rating === 'AA' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {celeb.rating}
                        </span>
                        {celeb.has_death && (
                          <span className="px-2 py-0.5 rounded text-xs bg-slate-600/50 text-slate-400">Deceased</span>
                        )}
                      </div>
                      {celeb.birth_name && celeb.birth_name !== celeb.name && (
                        <div className="text-xs text-slate-500 mb-1">Born: {celeb.birth_name}</div>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                        {celeb.birth_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {celeb.birth_date}
                          </span>
                        )}
                        {celeb.birth_city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {celeb.birth_city}, {celeb.birth_country}
                          </span>
                        )}
                        {celeb.events_count > 0 && (
                          <span className="text-amber-400">{celeb.events_count} life events</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {celeb.profession?.slice(0, 3).map((prof, i) => (
                          <span key={i} className="px-2 py-0.5 bg-slate-700/50 rounded text-xs text-slate-300">
                            {prof}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => window.open(`${API_URL}/api/celebrity-dataset/download-single/${celeb.id}`, '_blank')}
                      className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg transition-colors text-sm"
                      data-testid={`download-celeb-${celeb.id}`}
                      title="Download with Kernel"
                    >
                      <Download className="w-4 h-4" />
                      <span className="hidden sm:inline">Download</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="prev-page"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="px-4 py-2 text-sm">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="next-page"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Info Note */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl text-blue-300 text-sm">
          <strong>Note:</strong> Exporting with kernels for 24,000+ celebrities may take 30-60 minutes. 
          The export runs in the background - you can close this page and come back later.
        </div>
      </div>
    </div>
  );
}
